import React from "react";
import {
  Button,
  Title,
  useDataProvider,
  useNotify,
  useRedirect,
} from "react-admin";
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { ThirdPartyClient } from "../../models/ThirdPartyClient";
import type { Driver } from "../../models/Driver";

const DriverPickerDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  selected: Driver[];
  onConfirm: (drivers: Driver[]) => void;
}> = ({ open, onClose, selected, onConfirm }) => {
  const dataProvider = useDataProvider();
  const [loading, setLoading] = React.useState(false);
  const [nameInput, setNameInput] = React.useState("");
  const [phoneInput, setPhoneInput] = React.useState("");
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [page, setPage] = React.useState(1);
  const perPage = 10;
  const [rows, setRows] = React.useState<Driver[]>([]);
  const [total, setTotal] = React.useState(0);
  const [picked, setPicked] = React.useState<Map<string, Driver>>(
    new Map(selected.map((d) => [d.id, d]))
  );
  const openingRef = React.useRef(false);

  const allPageSelected = rows.length > 0 && rows.every((d) => picked.has(d.id));

  const load = React.useCallback(async () => {
    const phoneList = phone
      .split(/[,\s，]+/g)
      .map((s) => s.trim())
      .filter(Boolean);

    setLoading(true);
    try {
      const res = await dataProvider.getList("drivers", {
        pagination: { page, perPage },
        sort: { field: "id", order: "DESC" },
        filter: {
          ...(name ? { name } : {}),
          ...(phoneList.length === 1
            ? { phone: phoneList[0] }
            : phoneList.length > 1
              ? { phone: phoneList }
              : {}),
        },
      });
      setRows(res.data as Driver[]);
      setTotal(res.total ?? 0);
    } finally {
      setLoading(false);
    }
  }, [dataProvider, name, page, phone]);

  React.useEffect(() => {
    if (!open) return;
    openingRef.current = true;
    setPicked(new Map(selected.map((d) => [d.id, d])));
    setNameInput("");
    setPhoneInput("");
    setName("");
    setPhone("");
    setPage(1);
  }, [open, selected]);

  React.useEffect(() => {
    if (!open) return;
    if (openingRef.current) {
      if (page !== 1 || name !== "" || phone !== "") return;
      openingRef.current = false;
    }
    load();
  }, [load, name, open, page, phone]);

  const toggle = (driver: Driver) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(driver.id)) next.delete(driver.id);
      else next.set(driver.id, driver);
      return next;
    });
  };

  const onOk = () => {
    onConfirm(Array.from(picked.values()));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>选择司机</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="姓名"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              size="small"
            />
            <TextField
              label="手机号"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              size="small"
              multiline
              maxRows={4}
              helperText="支持多个手机号，使用逗号/空格/换行分隔"
              sx={{ minWidth: 260 }}
            />
            <Button
              disabled={loading}
              onClick={() => {
                setName(nameInput.trim());
                setPhone(phoneInput);
                setPage(1);
              }}
              label="搜索"
            />
            <Button
              disabled={loading}
              onClick={() => {
                setNameInput("");
                setPhoneInput("");
                setName("");
                setPhone("");
                setPage(1);
              }}
              label="重置"
            />
            <Button
              disabled={loading || rows.length === 0}
              onClick={() => {
                setPicked((prev) => {
                  const next = new Map(prev);
                  if (allPageSelected) {
                    rows.forEach((d) => next.delete(d.id));
                    return next;
                  }
                  rows.forEach((d) => next.set(d.id, d));
                  return next;
                });
              }}
              label={allPageSelected ? "取消全选本页" : "全选本页"}
            />
          </Stack>

          <Paper variant="outlined">
            <Box sx={{ p: 1, minHeight: perPage * 42 + 24 }}>
              {loading ? (
                <Stack spacing={1}>
                  {Array.from({ length: perPage }).map((_, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        pb: 1,
                      }}
                    >
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="rounded" width={20} height={20} />
                    </Box>
                  ))}
                </Stack>
              ) : rows.length > 0 ? (
                <Stack spacing={1}>
                  {rows.map((d) => (
                    <Box
                      key={d.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        pb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {d.name}（{d.phone}）
                      </Typography>
                      <Checkbox
                        size="small"
                        checked={picked.has(d.id)}
                        onChange={() => toggle(d)}
                        inputProps={{
                          "aria-label": picked.has(d.id) ? "取消选择" : "选择",
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2">无数据</Typography>
              )}
            </Box>
          </Paper>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              label="上一页"
            />
            <Typography variant="body2">
              第 {page} 页 / 共 {Math.max(1, Math.ceil(total / perPage))} 页
            </Typography>
            <Button
              disabled={page >= Math.ceil(total / perPage)}
              onClick={() => setPage((p) => p + 1)}
              label="下一页"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} label="取消" />
        <Button onClick={onOk} label={`确认（已选${picked.size}）`} />
      </DialogActions>
    </Dialog>
  );
};

export const ThirdDataRequestCreateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}> = ({ open, onClose, onSuccess }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [clientId, setClientId] = React.useState("");
  const [clients, setClients] = React.useState<ThirdPartyClient[]>([]);
  const [selectedDrivers, setSelectedDrivers] = React.useState<Driver[]>([]);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    dataProvider
      .getList("thirdPartyClients", {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "id", order: "DESC" },
        filter: {},
      })
      .then((res) => setClients(res.data as ThirdPartyClient[]))
      .catch(() => setClients([]));
  }, [dataProvider, open]);

  React.useEffect(() => {
    if (!open) return;
    setClientId("");
    setSelectedDrivers([]);
    setSubmitting(false);
    setPickerOpen(false);
  }, [open]);

  const onSubmit = async () => {
    if (!clientId) {
      notify("thirdDataRequestSelectClientFirst", { type: "warning" });
      return;
    }
    if (selectedDrivers.length === 0) {
      notify("thirdDataRequestSelectDriversFirst", { type: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await dataProvider.create("thirdDataRequests", {
        data: {
          clientId,
          userIds: selectedDrivers.map((d) => d.id),
        },
      });
      void res;
      notify("thirdDataRequestSubmitted", { type: "success" });
      onSuccess?.();
      onClose();
    } catch {
      notify("thirdDataRequestSubmitFailed", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>创建预授权</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              SelectProps={{ native: true }}
              label="客户端"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              size="small"
              fullWidth
            >
              <option value="" />
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}（{c.id}）
                </option>
              ))}
            </TextField>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px dashed rgba(148, 163, 184, 0.4)",
                backgroundColor: "rgba(15, 23, 42, 0.02)",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  已选择司机：{selectedDrivers.length}
                </Typography>
                <Button onClick={() => setPickerOpen(true)} label="选择司机" />
              </Stack>
              {selectedDrivers.length > 0 ? (
                <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                  {selectedDrivers.map((d) => (
                    <Chip
                      key={d.id}
                      label={`${d.name ?? "-"}（${d.phone ?? "-"}）`}
                      onDelete={() =>
                        setSelectedDrivers((prev) => prev.filter((x) => x.id !== d.id))
                      }
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  请选择需要授权的司机
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={submitting} onClick={onClose} label="取消" />
          <Button
            disabled={submitting}
            onClick={onSubmit}
            label={submitting ? "提交中..." : "确认创建"}
          />
        </DialogActions>
      </Dialog>

      <DriverPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selected={selectedDrivers}
        onConfirm={setSelectedDrivers}
      />
    </>
  );
};

export const ThirdDataRequestCreatePage: React.FC = () => {
  const redirect = useRedirect();

  return (
    <Box sx={{ p: 2 }}>
      <Title title="创建预授权" />
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            当前页面保留为兼容入口，实际创建能力已统一为弹窗形式。
          </Typography>
          <Button label="返回预授权列表" onClick={() => redirect("/thirdDataRequests")} />
        </Stack>
      </Paper>
      <ThirdDataRequestCreateDialog open={true} onClose={() => redirect("/thirdDataRequests")} />
    </Box>
  );
};
